/**
 * useGmDeskValue - one value kept on the GM's desk: the hidden half of
 * something whose other half the table sees.
 *
 * Read and written by the GM alone (the rules refuse anyone else, and a
 * seat that is not the GM's listens to nothing and holds `null`). The value
 * is set here at once and then written; a read that arrives while a write
 * is in flight is the desk as it was, and is not taken. Outside a session
 * the value is local to this screen.
 *
 * @param {{ sessionId: string|null, isGm: boolean, mp: { onGmDeskUpdate: Function, setGmDesk: Function } }} seat
 * @param {string} id - the value's name on the desk
 * @returns {[unknown, (value: unknown) => void]}
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { diag, errorSummary } from '../diagnostics/log';

export default function useGmDeskValue({ sessionId, isGm, mp }, id) {
  const [value, setValue] = useState(null);
  const inFlight = useRef(0);
  const kept = sessionId && isGm ? sessionId : null;

  useEffect(() => {
    if (!kept) return undefined;
    let live = true;
    const unsub = mp.onGmDeskUpdate(kept, id, (stored) => {
      if (live && inFlight.current === 0) setValue(stored ?? null);
    });
    return () => {
      live = false;
      unsub();
    };
  }, [kept, id, mp]);

  const set = useCallback(
    (next) => {
      setValue(next);
      if (!kept) return;
      inFlight.current += 1;
      mp.setGmDesk(kept, id, next)
        // Said, never thrown - and never with the value: the diagnostic log is readable by the table.
        .catch((e) => diag.error('gmDesk', 'a desk value did not save', errorSummary(e)))
        .finally(() => {
          inFlight.current -= 1;
        });
    },
    [kept, id, mp],
  );
  return [value, set];
}
